// Mixins
import { socialClubMixins } from '@/app/mixins/components/social-club-mixins';
import { userMixins } from '@/app/mixins/modules/user-mixins';
import { toastMessageMixins } from '@/app/mixins/utils/toast-message-mixins';
import { imageCompressorMixins } from '@/app/mixins/utils/image-compressor-mixins';

// Services
import PostService from '@/app/services/main/post/post-service.js';

// Constants
import { ERROR_MSG } from '@/app/constants/status-constants';
import { STATUS_CODES } from '@/app/constants/status-constants';

// Third party libraries
import * as $ from 'jquery';
const vue2Dropzone = () => import(/*webpackPrefetch: true, webpackChunkName: "vue-dropzone"*/ 'vue2-dropzone');

export default {
    name: 'ModalSocialClubEditPost',
    components: {
        vueDropZone: vue2Dropzone
    },
    mixins: [
        socialClubMixins,
        userMixins,
        toastMessageMixins,
        imageCompressorMixins
    ],
    props: {
        postContent: {
            type: Object,
            required: true
        },
        firstOpenFlag: {
            type: Boolean,
            required: true,
            default: true
        }
    },
    data() {
        return {
            dropOptions: {
                url: 'https://httpbin.org/post', // endpoint where files needs to be sent. Currently mocked.
                thumbnailWidth: null,
                thumbnailHeight: null,
                maxFilesize: 20,
                maxThumbnailFilesize: 20,
                acceptedFiles: '.svg, .png, .jpg, .gif, .jpeg',
                headers: { 'Authorization': 'Bearer mock authorization' },
                uploadMultiple: true,
                autoProcessQueue: false,
                maxFiles: 6,
                addRemoveLinks: true,
                clickable: `#editPostUploader-${this.postContent.id}`,
                dictFileTooBig: 'File Size must not exceed 20 MB.',
                dictInvalidFileType: 'Unsupported File Format.',
                dictRemoveFile: "<i class='fa fa-close'></i>",
                dictDefaultMessage: "<i class='fa fa-cloud-upload mr-2'></i> Drag images to upload. Maximum of 6 images."
            },
            isPostModalLoading: false,
            content: this.postContent.content,
            newFileCount: 0,
            removeImages: [],
            isUpdatingPost: false,
            hasChangeFlag: false,
            onDragStyle: false,
            isBehind: false
        }
    },
    computed: {
        isSameContent() {
            const isSame = this.content === this.postContent.content && this.newFileCount === 0 && this.removeImages.length === 0;

            if (!isSame) {
                this.hasChangeFlag = true;
            }

            return isSame;
        },
        displayUploadText() {
            return this.removeImages.length === this.postContent.mediaFiles.length;
        }
    },
    mounted() {
        $(this.$refs.editPostModal).on('hide.bs.modal', this.handleHideEvent);
    },
    beforeDestroy() {
        this.hasChangeFlag = false;
        this.closeModal();
        $(this.$refs.editPostModal).off('hide.bs.modal', this.handleHideEvent);
    },
    methods: {
        handleHideEvent(e) {
            if (this.hasChangeFlag && !this.isSameContent) {
                e.preventDefault();
                this.$swal({
                    imageHeight: '30px',
                    title: 'You have unsaved changes!',
                    text: 'Are you sure you want to close this modal?',
                    imageUrl: `${require('@/assets/images/_shared/dog-confirm.png')}`,
                    showCancelButton: true,
                    confirmButtonColor: '#4B8BFF',
                    cancelButtonColor: '#d33',
                    confirmButtonText: 'Proceed',
                    reverseButtons: true
                }).then(result => {
                    if (result.value) {
                        this.hasChangeFlag = false;
                        this.closeModal();
                    }
                });
            }
        },
        openModal() {
            this.resetData();
            const { mediaFiles } = this.postContent;
            if (mediaFiles.length) {
                mediaFiles.forEach(({ location, key }) => {
                    this.$refs.editPostDropZone.manuallyAddFile({ size: 5, name: key, type: 'image/png' }, location);
                });

                this.triggerOpenFlag(false);
            }

            $(this.$refs.editPostModal).modal('show');
        },
        triggerOpenFlag(flag) {
            this.$emit('triggerOpenFlag', flag);
        },
        closeModal() {
            this.resetData();
            $(this.$refs.editPostModal).modal('hide');
            this.$store.commit('resetEditPost');
        },
        async editPost() {
            const postCopy = { ...this.postContent };
            const addingImages = this.$refs.editPostDropZone.getAcceptedFiles();
            const postDetails = {
                formDetails: { postId: this.postContent.id }
            };

            if (this.content !== this.postContent.content) {
                postDetails.formDetails.content = this.content;
                postCopy.content = this.content;
            }

            if (this.removeImages.length > 0) {
                postDetails.formDetails.removeImage = this.removeImages;
                postCopy.mediaFiles = postCopy.mediaFiles.filter(({ key }) => !this.removeImages.includes(key));
            }

            // compress images
            postDetails.images = await this.compressMultipleImages(addingImages);
       
            if(!postCopy.content && postCopy.mediaFiles.length === 0){
                this.showDeleteAlert(postCopy)
            } else {
                this.isUpdatingPost = true;
                const res = await PostService.updatePost(postDetails);
                if (res.status === STATUS_CODES.SUCCESS) {
                    const { data: { post: updatedPost } } = res;
    
                    // Concat the newly added images
                    postCopy.mediaFiles = updatedPost.mediaFiles;
    
                    this.$emit('updatePostCallback', postCopy);
                    this.resetData();
                    this.closeModal();
                } else {
                    const { ERROR_UPDATE_POST } = this.toastMessages.post();
                    this.showAPIErrorToast(ERROR_UPDATE_POST, res);
                }
                this.isUpdatingPost = false;
            }

        },
        resetData() {
            this.removeImages = [];
            this.newFileCount = 0;
            this.content = this.postContent.content;
            this.hasChangeFlag = false;
            this.triggerOpenFlag(true);
            // reset dropzone when post is edited.
            this.$refs.editPostDropZone.removeAllFiles();
        },
        onAddFile(file) {
            if (!file.manuallyAdded) {
                this.newFileCount++;
            }
        },
        onRemoveFile(file) {
            if (file.manuallyAdded) {
                // If the file is manually added it means the user
                // wants to remove this image. Get the prop 
                // `name` that contains the `key` id of the image.
                this.removeImages = this.removeImages.concat(file.name);
            } else {
                // If the file is not manually added it means the user
                // just add this file and remove it
                this.newFileCount--;
            }
        },
        showDeleteAlert(postCopy) {
            this.$swal({
                imageHeight: '30px',
                imageUrl: `${require('@/assets/images/_shared/dog-confirm.png')}`,
                imageAlt: 'Confirm Dog',
                titleText: `Delete Post`,
                html: `<p>Post without content will be deleted, would you like to proceed?</p>`,
                confirmButtonText: 'Confirm',
                cancelButtonText: 'Cancel',
                showCancelButton: true,
                customClass: {
                    header: 'header-social-club',
                    title: 'title-social-club'
                },
                reverseButtons: true
            })
                .then(result => {
                    if (result.isConfirmed) {
                        this.isUpdatingPost = true;
                        this.$emit('deletePostCallback',postCopy.id);
                    }
                });
        },
        onDropZoneError(file, message) {
            if (message !== ERROR_MSG.DROPZONE_UPLOAD_CANCELLED) {
                this.showCustomErrorToast(ERROR_MSG.DROPZONE_UPLOAD_ERROR_TITLE, message);
            }

            this.$refs.editPostDropZone.removeFile(file);
        },
        onDropZoneOver() {
            this.onDragStyle = true;
        },
        onDragEnter() {
            this.isBehind = true;
        }
    }
}